import { COLORS, LIFT_PARTS, PEOPLE } from '../theme.js';
import { el } from './dom.js';

/**
 * The street the buildings stand on, and the sky above them (home screen).
 *
 * Every building is on the same street. The street strip is fixed along the
 * bottom of the home screen and never redrawn when the player pages between
 * buildings: people keep walking and cars keep driving while the building
 * changes, and the paving and lane markings slide past as the building does,
 * so a page turn feels like walking along the street to the next door. The sky
 * is the same: clouds drift, birds, a plane and a helicopter cross now and then,
 * and it slides a little, further away than the street.
 *
 * Presentation only, and quiet on purpose: the colours are the people's own
 * muted palette and the rooms' furniture, so nothing out here is louder than a
 * ticket (ADR-0018), and `STREET_ART` is measured by the same test as rooms.
 * Under reduced motion it all holds still.
 */

const css = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;
const INK = css(COLORS.ink);
const CREAM = css(COLORS.cabin);
const GLASS = '#d6e6ee';
const STEEL = css(LIFT_PARTS.steel);
const IRON = css(LIFT_PARTS.iron);
const GLOW = css(LIFT_PARTS.glow);
const ROSE = css(LIFT_PARTS.rug);
const LEAF = '#7fa874';
const TERRACOTTA = '#c9774f';
const SLATE = '#8fa3b0';
const COATS = PEOPLE.coats.map(css);
const SKINS = PEOPLE.skin.map(css);
const HAIRS = PEOPLE.hair.map(css);
const LEGS = css(PEOPLE.legs);

/** The ground's own colours, used by the stylesheet (kept here to be measured). */
export const GROUND = {
  kerbTop: '#b9a888',
  pavement: '#ddd0b8',
  joint: '#c8b99c',
  kerb: '#a99a80',
  road: '#7a726b',
  dash: '#e9dfc8',
  farKerb: '#c8bba2',
} as const;

const line = `stroke="${INK}" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"`;

function svg(viewBox: string, width: number, height: number, inner: string): string {
  return `<svg viewBox="${viewBox}" width="${width}" height="${height}" aria-hidden="true">${inner}</svg>`;
}

function wheel(cx: number, cy: number): string {
  return `<circle cx="${cx}" cy="${cy}" r="3.6" fill="${INK}"/><circle cx="${cx}" cy="${cy}" r="1.4" fill="${STEEL}"/>`;
}

const VEHICLES = {
  car: (body: string): string =>
    svg(
      '0 0 48 22',
      48,
      22,
      `<path d="M3 15V11q0-2 2-2l7-1 5-5q1-1 3-1h11q2 0 3 1l5 5 5 1q2 .5 2 3v3q0 2-2 2H5q-2 0-2-2z" fill="${body}" ${line}/>
       <path d="M18.5 4H25v4H14.5zM27 4h5l4 4h-9z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
       <rect x="43" y="10.5" width="3" height="2" rx="1" fill="${GLOW}"/>${wheel(13, 17)}${wheel(37, 17)}`,
    ),
  // The hotel's cab: dark, with its lamp lit.
  taxi: (): string =>
    svg(
      '0 0 48 24',
      48,
      24,
      `<rect x="21" y="1" width="7" height="3" rx="1" fill="${GLOW}" stroke="${INK}" stroke-width="1"/>
       <path d="M3 17v-4q0-2 2-2l7-1 5-5q1-1 3-1h11q2 0 3 1l5 5 5 1q2 .5 2 3v3q0 2-2 2H5q-2 0-2-2z" fill="${IRON}" ${line}/>
       <path d="M18.5 6H25v4H14.5zM27 6h5l4 4h-9z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
       <rect x="43" y="12.5" width="3" height="2" rx="1" fill="${GLOW}"/>${wheel(13, 19)}${wheel(37, 19)}`,
    ),
  // The office tower's delivery van.
  van: (): string =>
    svg(
      '0 0 54 26',
      54,
      26,
      `<path d="M3 19V5q0-2 2-2h28q2 0 2 2v3h7l7 6q2 1.5 2 3.5V19q0 2-2 2H5q-2 0-2-2z" fill="${CREAM}" ${line}/>
       <path d="M37 10h5l5 4.5h-10z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
       <rect x="6" y="9" width="24" height="5" rx="1.5" fill="${TERRACOTTA}"/>
       <rect x="49" y="15" width="3" height="2" rx="1" fill="${GLOW}"/>${wheel(13, 21)}${wheel(43, 21)}`,
    ),
  // The transit centre's bus.
  bus: (): string =>
    svg(
      '0 0 92 30',
      92,
      30,
      `<path d="M3 23V6q0-3 3-3h78q4 0 5 4l1 8v8q0 2-2 2H5q-2 0-2-2z" fill="${CREAM}" ${line}/>
       <rect x="3" y="17" width="87" height="3" fill="${LEAF}"/>
       <path d="M9 7h11v7H9zM23 7h11v7H23zM37 7h11v7H37zM51 7h11v7H51zM65 7h11v7H65zM79 7h7l2 7h-9z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
       <rect x="86" y="19" width="3" height="2" rx="1" fill="${GLOW}"/>${wheel(17, 25)}${wheel(73, 25)}`,
    ),
  // Skyline Tower's limousine.
  limo: (): string =>
    svg(
      '0 0 70 22',
      70,
      22,
      `<path d="M3 15v-4q0-2 2-2l9-1 5-5q1-1 3-1h31q2 0 3 1l5 5 5 1q2 .5 2 3v3q0 2-2 2H5q-2 0-2-2z" fill="#2f2a30" ${line}/>
       <path d="M20.5 4H30v4H16.5zM32 4h10v4H32zM44 4h9l4 4H44z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
       <rect x="65" y="10.5" width="3" height="2" rx="1" fill="${GLOW}"/>${wheel(14, 17)}${wheel(58, 17)}`,
    ),
};

function walker(i: number): string {
  const coat = COATS[i % COATS.length] as string;
  const skin = SKINS[(i * 3 + 1) % SKINS.length] as string;
  const hair = HAIRS[(i * 5 + 2) % HAIRS.length] as string;
  return svg(
    '0 0 18 36',
    18,
    36,
    `<g class="stride-a"><rect x="5.5" y="21" width="3" height="14" rx="1.5" fill="${LEGS}"/></g>
     <g class="stride-b"><rect x="9.5" y="21" width="3" height="14" rx="1.5" fill="${LEGS}"/></g>
     <rect x="3.5" y="10.5" width="11" height="14" rx="4.5" fill="${coat}" ${line}/>
     <circle cx="9" cy="6.2" r="4.6" fill="${skin}" ${line}/>
     <path d="M4.6 5.6q0-4.4 4.4-4.4t4.4 4.4q-1.4-2.4-4.4-2.4t-4.4 2.4z" fill="${hair}"/>`,
  );
}

const CLOUD = (w: number): string =>
  svg(
    '0 0 100 40',
    w,
    w * 0.4,
    `<path d="M14 36a12 12 0 0 1 4-23 16 16 0 0 1 30-5 13 13 0 0 1 24 4 11 11 0 0 1 14 24z" fill="${CREAM}"/>`,
  );

const BIRD = `<g class="wing"><path d="M0 4q3-4 6 0 3-4 6 0" fill="none" stroke="${INK}" stroke-width="1.4" stroke-linecap="round"/></g>`;

const FLOCK = `<svg viewBox="0 0 40 16" width="40" height="16" aria-hidden="true">
  <g transform="translate(0 6)">${BIRD}</g><g transform="translate(14 0)">${BIRD}</g><g transform="translate(26 8)">${BIRD}</g></svg>`;

const PLANE = svg(
  '0 0 56 18',
  56,
  18,
  `<path d="M4 9q0-3 4-3h34l6-4h4l-3 7q2 1 2 2t-3 2H8q-4 0-4-4z" fill="#efe9dc" ${line}/>
   <path d="M22 11l6 6h4l-2-6z" fill="${SLATE}" ${line}/>
   <path d="M12 8h2M17 8h2M22 8h2M27 8h2M32 8h2" stroke="${INK}" stroke-width="1.4" stroke-linecap="round"/>`,
);

const HELI = svg(
  '0 0 56 30',
  56,
  30,
  `<g class="rotor"><path d="M6 4h40" stroke="${INK}" stroke-width="2" stroke-linecap="round"/></g>
   <path d="M26 4v4" stroke="${INK}" stroke-width="1.6"/>
   <path d="M16 18q0-10 12-10t12 9q0 4-5 5H20q-4 0-4-4z" fill="${ROSE}" ${line}/>
   <path d="M30 10q7 0 8 7h-8z" fill="${GLASS}" stroke="${INK}" stroke-width="1"/>
   <path d="M16 14H3l-1-4" fill="none" stroke="${INK}" stroke-width="2.2" stroke-linecap="round"/>
   <path d="M18 26h20M22 22v4M34 22v4" stroke="${INK}" stroke-width="1.4" stroke-linecap="round"/>`,
);

/** Every piece of art out here, for the colour test. */
export const STREET_ART: string = [
  ...Object.values(GROUND),
  ...[...COATS, SLATE, ROSE].map((c) => VEHICLES.car(c)),
  VEHICLES.taxi(),
  VEHICLES.van(),
  VEHICLES.bus(),
  VEHICLES.limo(),
  ...[0, 1, 2, 3, 4].map(walker),
  CLOUD(100),
  FLOCK,
  PLANE,
  HELI,
].join('\n');

/** An actor crossing the screen: how long a crossing takes, and where in it it starts. */
function actor(
  className: string,
  art: string,
  seconds: number,
  startShare: number,
  extra: Record<string, string> = {},
): HTMLElement {
  const node = el('div', { class: className, ...extra });
  node.innerHTML = art;
  node.style.setProperty('--t', `${seconds}s`);
  node.style.setProperty('--d', `${-seconds * startShare}s`);
  return node;
}

export interface Street {
  readonly sky: HTMLElement;
  readonly street: HTMLElement;
  /** Which building is in front, and what is next door if it is not reached yet. */
  setBuilding(id: string, works: string | null): void;
  /** The finger has moved the building `offset` px. */
  drag(offset: number): void;
  /** The building settled back where it was. */
  settle(): void;
  /** The view walked one building along: -1 toward the next, +1 back. */
  travel(direction: -1 | 1): void;
  /** True while a travel is still under way. */
  readonly travelling: boolean;
}

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function buildStreet(): Street {
  // ---- the sky: clouds far away, then birds, a plane and a helicopter
  const clouds = el('div', { class: 'sky-far' }, [
    actor('cloud', CLOUD(110), 150, 0.15, { style: 'top:8%' }),
    actor('cloud', CLOUD(70), 110, 0.55, { style: 'top:22%;opacity:.75' }),
    actor('cloud', CLOUD(90), 130, 0.85, { style: 'top:38%;opacity:.85' }),
    actor('cloud', CLOUD(60), 95, 0.35, { style: 'top:52%;opacity:.7' }),
  ]);
  const flock = actor('flock', FLOCK, 34, 0.1, { style: 'top:16%' });
  const sky = el('div', { class: 'home-sky', 'aria-hidden': 'true' }, [
    clouds,
    el('div', { class: 'sky-near' }, [
      flock,
      actor('plane', PLANE, 60, 0.6, { style: 'top:6%' }),
      actor('heli', HELI, 48, 0.25, { style: 'top:30%' }),
    ]),
  ]);

  // ---- the street: paving, road, people, traffic
  const ground = el('div', { class: 'street-ground' });
  const walkers = el('div', { class: 'street-walk' }, [
    actor('walker east', walker(0), 26, 0.1),
    actor('walker west', walker(1), 31, 0.4),
    actor('walker east', walker(2), 23, 0.62),
    actor('walker west', walker(3), 28, 0.85),
    actor('walker east', walker(4), 34, 0.33),
  ]);
  const far = el('div', { class: 'lane far' }, [
    actor('vehicle west', VEHICLES.car(COATS[3] as string), 9, 0.2),
    actor('vehicle west', VEHICLES.car(SLATE), 11, 0.7),
    actor('vehicle west', VEHICLES.bus(), 14, 0.45, { 'data-for': 'transit-centre' }),
  ]);
  const near = el('div', { class: 'lane near' }, [
    actor('vehicle east', VEHICLES.car(COATS[1] as string), 8, 0.05),
    actor('vehicle east', VEHICLES.car(ROSE), 10, 0.5),
    actor('vehicle east', VEHICLES.car(COATS[4] as string), 12, 0.8),
    actor('vehicle east', VEHICLES.taxi(), 9, 0.3, { 'data-for': 'grand-hotel' }),
    actor('vehicle east', VEHICLES.van(), 11, 0.62, { 'data-for': 'office-tower' }),
    actor('vehicle east', VEHICLES.limo(), 13, 0.4, { 'data-for': 'skyline-tower' }),
  ]);
  const works = el('div', { class: 'street-works' });
  const street = el('div', { class: 'home-street', 'aria-hidden': 'true' }, [
    ground,
    walkers,
    far,
    near,
    works,
  ]);

  // ---- the walk along the street
  let base = 0;
  let shown = 0;
  let frame = 0;
  let travelling = false;
  const show = (x: number): void => {
    shown = x;
    ground.style.setProperty('--ground-x', `${x}px`);
  };
  const ease = (from: number, to: number, ms: number, done?: () => void): void => {
    cancelAnimationFrame(frame);
    if (reducedMotion()) {
      show(to);
      done?.();
      return;
    }
    const start = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / ms);
      show(from + (to - from) * (1 - (1 - t) ** 3));
      if (t < 1) frame = requestAnimationFrame(step);
      else done?.();
    };
    frame = requestAnimationFrame(step);
  };
  return {
    sky,
    street,
    get travelling() {
      return travelling;
    },
    setBuilding(id, nextName) {
      street.dataset.building = id;
      works.replaceChildren();
      if (nextName) {
        works.append(
          el('span', { class: 'works-sign' }, [el('b', { text: nextName }), 'under construction']),
          el('span', { class: 'works-barrier' }),
        );
      }
    },
    drag(offset) {
      cancelAnimationFrame(frame);
      show(base + offset);
      sky.style.transition = 'none';
      sky.style.transform = `translateX(${offset * 0.12}px)`;
    },
    settle() {
      ease(shown, base, 200);
      sky.style.transition = 'transform 0.25s ease-out';
      sky.style.transform = '';
    },
    travel(direction) {
      const width = street.clientWidth || 360;
      base += direction * width;
      travelling = true;
      ease(shown, base, 520, () => {
        travelling = false;
      });
      sky.style.transition = 'transform 0.6s ease-out';
      sky.style.transform = '';
    },
  };
}
